import { tokenList, type TokenMeta } from "@one-nexty/tokens";

/**
 * Sélecteurs sur la liste de tokens.
 *
 * La documentation ne contient AUCUNE valeur en dur : tout ce qui est affiché
 * plus loin est lu ici. Ajouter une nuance dans `colors.json` la fait apparaître
 * dans la vitrine sans toucher au code React.
 */

/** Palette primitive : `color.primary.500` → profondeur 3. */
export const primitiveColors = tokenList.filter(
  (token) => token.path[0] === "color" && token.path.length === 3,
);

/** Tokens sémantiques : `color.surface` → profondeur 2, avec variante sombre. */
export const semanticColors = tokenList.filter(
  (token) => token.path[0] === "color" && token.path.length === 2,
);

/** Familles de la palette, dans l'ordre du fichier source. */
export const colorFamilies: { name: string; shades: TokenMeta[] }[] = (() => {
  const groups = new Map<string, TokenMeta[]>();
  for (const token of primitiveColors) {
    const family = token.path[1] ?? "autre";
    const shades = groups.get(family);
    if (shades) shades.push(token);
    else groups.set(family, [token]);
  }
  return [...groups].map(([name, shades]) => ({ name, shades }));
})();

export const fontSizes = tokenList.filter(
  (token) => token.path[0] === "font" && token.path[1] === "size",
);

export const fontFamilies = tokenList.filter(
  (token) => token.path[0] === "font" && token.path[1] === "family",
);

export const fontWeights = tokenList.filter(
  (token) => token.path[0] === "font" && token.path[1] === "weight",
);

/** `base` est exclue : c'est le pas de la grille, pas une valeur de l'échelle. */
export const spacings = tokenList.filter(
  (token) => token.path[0] === "spacing" && token.path[1] !== "base",
);

export const radii = tokenList.filter((token) => token.path[0] === "radius");

export const shadows = tokenList.filter((token) => token.path[0] === "shadow");

export const durations = tokenList.filter(
  (token) => token.path[0] === "motion" && token.path[1] === "duration",
);

export const easings = tokenList.filter(
  (token) => token.path[0] === "motion" && token.path[1] === "ease",
);

/**
 * `"cubic-bezier(0.2, 0, 0, 1)"` → `[0.2, 0, 0, 1]`.
 * Renvoie `null` si la valeur n'est pas une courbe de Bézier (ex: `linear`),
 * auquel cas il n'y a simplement rien à tracer.
 */
export function parseCubicBezier(value: string): [number, number, number, number] | null {
  const match = /^cubic-bezier\(([^)]+)\)$/.exec(value.trim());
  if (!match?.[1]) return null;

  const points = match[1].split(",").map((part) => Number(part.trim()));
  return points.length === 4 && points.every((n) => Number.isFinite(n))
    ? (points as [number, number, number, number])
    : null;
}

/** Dernier segment du chemin — sert de libellé court (`500`, `md`, `surface-raised`). */
export const leaf = (token: TokenMeta): string => token.path[token.path.length - 1] ?? token.name;

/** Nombre total de tokens, affiché dans le hero. */
export const tokenCount = tokenList.length;

/**
 * Nombre de composants exposés par @one-nexty/components-web.
 *
 * Compté à la main plutôt que dérivé des exports : `Card` et ses cinq
 * sous-parties forment un seul composant du point de vue de l'utilisateur,
 * et un `Object.keys()` en compterait six.
 */
export const componentCount = 32;
